/*
 * WF first-party Add-Ons PDP widget.
 *
 * Resolves the matching add-on set from the worker and renders collapsible
 * category accordions with per-item quantity steppers + a live price preview.
 *
 * Add-to-cart: when the customer has selected add-ons, the widget intercepts the
 * ATC click (capture phase) and posts ONE atomic /cart/add.js items[] request:
 *   - parent line: the PDP variant + properties Add-Ons (print summary, default
 *     "No, Thank you"), _wf_addons JSON [{t,q,p}], and _wf_addon_group (uuid).
 *   - child lines: each selected add-on variant with quantity, parent_id, and
 *     properties _wf_addon_group (shared), _wf_addon_child, For (parent title).
 *   For checkoutMode "children_only" the parent line is omitted.
 * (We do our own add rather than Eurus's _linked_product_id path, which does not
 *  materialise the child lines in this theme build.)
 *
 * A required "None, Thank You" gate blocks add-to-cart until the customer picks
 * >=1 add-on or opts out (opt-out lets Eurus do its normal add; the form carries
 * Add-Ons = "No, Thank you"). Strict no-op when no set resolves: never touches ATC.
 *
 * Docs: docs/addons.md. Worker: /api/addons/public. Cart contract: cartContract.ts.
 */
(function () {
  'use strict';

  var API_BASE = 'https://planners.dand3.com';

  // Delay before the post-add redirect so Shopify's Web Pixel can finish emitting
  // the native `product_added_to_cart` event (async sandbox postMessage ->
  // sendBeacon) before navigation tears the page down. Without it the beacon is
  // dropped and GA4 undercounts add_to_cart on gated products (~37% low) even
  // though the item really is in the cart. No public flush API exists; 500ms
  // covers the postMessage hop with negligible UX lag.
  var ADD_TO_CART_BEACON_MS = 500;

  function money(cents) {
    var n = (Number(cents) || 0) / 100;
    return '$' + n.toFixed(2);
  }

  function escapeAttr(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  function el(tag, cls, text) {
    var node = document.createElement(tag);
    if (cls) node.className = cls;
    if (text != null) node.textContent = text;
    return node;
  }

  function init(root) {
    if (root.__wfAddonsInit) return;
    root.__wfAddonsInit = true;

    var storeKey = root.dataset.storeKey || 'WF';
    var productId = root.dataset.productId;
    if (!productId) return;

    var formId = root.dataset.formId;
    var sectionId = root.dataset.sectionId;
    var productTitle = root.dataset.productTitle || '';
    var body = root.querySelector('[data-wf-addons-body]');
    var basePrice = parseInt(root.dataset.basePrice, 10) || 0;

    var variantPrices = {};
    try {
      var raw = root.querySelector('.wf-addons-variant-prices');
      if (raw) variantPrices = JSON.parse(raw.textContent);
    } catch (e) { /* preview-only feature; ignore */ }

    var variantTitles = {};
    try {
      var titlesNode = root.querySelector('.wf-addons-variant-titles');
      if (titlesNode) variantTitles = JSON.parse(titlesNode.textContent);
    } catch (err) { console.warn('[wf-addons] variant titles unavailable', err); }

    var set = null;
    var state = {};        // itemId -> { qty, variantId, title, price }
    var optedOut = false;
    var fieldValues = {};  // fieldId -> string value

    var url = API_BASE + '/api/addons/public'
      + '?storeKey=' + encodeURIComponent(storeKey)
      + '&productId=' + encodeURIComponent(productId)
      + '&tags=' + encodeURIComponent(root.dataset.tags || '')
      + '&collections=' + encodeURIComponent(root.dataset.collections || '');

    // Height memo read by snippets/wf-product-addons.liquid on the next view, so the widget
    // reserves its space at first paint instead of shoving the buy box down ~1.5s in. Stores 0
    // for products that resolve to nothing, so those never reserve.
    function rememberHeight(px) {
      try {
        window.localStorage.setItem('wf-addon-h1:' + productId, Math.round(px) + ':' + Date.now());
      } catch (e) { /* private mode / quota: memo is best-effort */ }
    }

    fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        var resolved = data && data.addonSet;
        if (!resolved) { rememberHeight(0); return; } // no-op
        var hasCats = resolved.categories && resolved.categories.length;
        var hasFields = resolved.fields && resolved.fields.length;
        if (!hasCats && !hasFields) { rememberHeight(0); return; } // nothing to render
        render(resolved);
      })
      .catch(function (err) { console.warn('[wf-addons] resolve failed', err); });

    // ---- ATC integration --------------------------------------------------

    function atcButtons() {
      return [
        document.getElementById('x-atc-button-' + sectionId),
        document.getElementById('x-atc-button-sticky-' + sectionId)
      ].filter(Boolean);
    }

    function selectedCount() {
      var n = 0;
      for (var k in state) { if (state[k].qty > 0) n++; }
      return n;
    }

    // Selected, cart-addable add-ons (qty > 0 and resolvable to a variant).
    function selectedList() {
      var arr = [];
      if (optedOut) return arr;
      for (var id in state) {
        var it = state[id];
        if (it.qty > 0 && it.variantId) arr.push(it);
      }
      return arr;
    }

    function uuid() {
      try { return crypto.randomUUID(); } catch (e) {}
      return 'wfao-' + Date.now().toString(36) + '-' + Math.floor(Math.random() * 1e9).toString(36);
    }

    function requiredFieldsFilled() {
      if (!set || !set.fields) return true;
      return set.fields.every(function (f) {
        if (!f.required) return true;
        var v = fieldValues[f.id];
        return v != null && String(v).trim() !== '';
      });
    }

    function gateSatisfied() {
      // Add-on choice gate only applies when the set has product categories.
      var hasCats = set && set.categories && set.categories.length > 0;
      var addonOk = !hasCats || optedOut || selectedCount() > 0;
      return addonOk && requiredFieldsFilled();
    }

    function applyGate() {
      var ok = gateSatisfied();
      root.classList.toggle('wf-product-addons--ungated', !ok);
      atcButtons().forEach(function (b) {
        if (ok) {
          b.removeAttribute('aria-disabled');
          b.classList.remove('wf-atc-gated');
        } else {
          b.setAttribute('aria-disabled', 'true');
          b.classList.add('wf-atc-gated');
        }
      });
      // Once an add-on is selected, disable "No thanks" so it can't contradict the
      // selection (you can't both add an add-on and opt out).
      var optEl = root.querySelector('.wf-addons-optout');
      if (optEl) {
        var hasSelection = selectedCount() > 0;
        optEl.disabled = hasSelection;
        optEl.classList.toggle('wf-addons-optout--disabled', hasSelection);
        optEl.setAttribute('aria-disabled', hasSelection ? 'true' : 'false');
      }
    }

    function flashHint() {
      var hint = root.querySelector('.wf-product-addons__hint');
      if (!hint) return;
      hint.classList.add('wf-product-addons__hint--flash');
      setTimeout(function () { hint.classList.remove('wf-product-addons__hint--flash'); }, 1200);
    }

    // Gated ATC tap: instead of silently no-op'ing, bring the "No thanks" opt-out
    // into focus + pulse it, and flash an explicit proceed hint. Keeps the upsell
    // gate intact but makes the path forward obvious.
    function nudgeOptOut() {
      var hint = root.querySelector('.wf-product-addons__hint');
      var optEl = root.querySelector('.wf-addons-optout');
      var canOptOut = optEl && !optEl.disabled;
      var hasCats = set && set.categories && set.categories.length > 0;
      (canOptOut ? optEl : root).scrollIntoView({ behavior: 'smooth', block: 'center' });
      if (hint) {
        if (hint.__wfDefaultText == null) hint.__wfDefaultText = hint.textContent;
        hint.textContent = canOptOut
          ? 'To add this to your cart, pick an add-on or tap "No thanks".'
          : (hasCats && selectedCount() === 0
              ? 'Please choose an option above to add to cart.'
              : 'Please complete the details above to add to cart.');
        hint.classList.add('wf-product-addons__hint--flash');
        clearTimeout(hint.__wfHintTimer);
        hint.__wfHintTimer = setTimeout(function () {
          hint.classList.remove('wf-product-addons__hint--flash');
          if (hint.__wfDefaultText != null) hint.textContent = hint.__wfDefaultText;
        }, 3200);
      }
      if (canOptOut) {
        optEl.classList.add('wf-addons-optout--nudge');
        try { optEl.focus({ preventScroll: true }); } catch (e) { optEl.focus(); }
        clearTimeout(optEl.__wfNudgeTimer);
        optEl.__wfNudgeTimer = setTimeout(function () {
          optEl.classList.remove('wf-addons-optout--nudge');
        }, 2400);
      }
    }

    // Capture-phase ATC intercept (attached once a set exists):
    //  - gate unsatisfied  -> block the add, nudge the customer to choose.
    //  - add-ons selected  -> own the add: atomic /cart/add.js (parent + children).
    //  - opted out / none  -> let Eurus add normally (form carries Add-Ons summary).
    function attachGuard() {
      atcButtons().forEach(function (b) {
        if (b.__wfAddonsGuard) return;
        b.__wfAddonsGuard = true;
        b.addEventListener('click', function (e) {
          if (!gateSatisfied()) {
            e.preventDefault();
            e.stopImmediatePropagation();
            nudgeOptOut();
            return;
          }
          if (selectedList().length > 0 || set.checkoutMode === 'children_only') {
            e.preventDefault();
            e.stopImmediatePropagation();
            if (b.getAttribute('aria-busy') === 'true') return;
            atomicAdd(b);
          }
          // else: opted out with no add-ons -> Eurus handles the normal add.
        }, true);
      });
    }

    // ---- Parent-line properties (Add-Ons summary + JSON) -------------------
    // Written to the form so the opt-out path (Eurus's own add) carries them too.

    function syncForm() {
      var form = document.getElementById(formId);
      if (!form) return;
      var holder = form.querySelector('.wf-addons-form-inputs');
      if (!holder) {
        holder = document.createElement('div');
        holder.className = 'wf-addons-form-inputs';
        holder.style.display = 'none';
        form.appendChild(holder);
      }
      var sel = selectedList();
      var summary = sel.map(function (it) { return it.qty > 1 ? it.title + ' x' + it.qty : it.title; });
      var jsonArr = sel.map(function (it) {
        return it.price != null ? { t: it.title, q: it.qty, p: it.price } : { t: it.title, q: it.qty };
      });
      var html = '';
      // Only emit the Add-Ons print summary when the set has product add-ons.
      if (set && set.categories && set.categories.length) {
        var addOnsVal = summary.length ? summary.join(', ') : 'No, Thank you';
        html += '<input type="hidden" name="properties[' + escapeAttr(set.cartPropertyName || 'Add-Ons') + ']" value="' + escapeAttr(addOnsVal) + '">';
        if (jsonArr.length) {
          html += '<input type="hidden" name="properties[_wf_addons]" value="' + escapeAttr(JSON.stringify(jsonArr)) + '">';
        }
      }
      // Custom field values → named line-item properties.
      if (set && set.fields) {
        set.fields.forEach(function (f) {
          var v = fieldValues[f.id];
          if (v != null && String(v).trim() !== '') {
            html += '<input type="hidden" name="properties[' + escapeAttr(f.propertyName) + ']" value="' + escapeAttr(v) + '">';
          }
        });
      }
      holder.innerHTML = html;
    }

    // ---- Atomic add-to-cart (parent + selected add-on child lines) ---------

    // Collect the parent line's existing form properties (Add-Ons, _wf_addons,
    // plus any Eurus props like gift-card recipient), excluding our own holder
    // duplicates which we re-add explicitly.
    function gatherParentProps(form) {
      var props = {};
      form.querySelectorAll('[name^="properties["]').forEach(function (elx) {
        if (elx.disabled) return;
        var m = elx.name.match(/^properties\[(.+)\]$/);
        if (!m) return;
        var key = m[1];
        if (key.indexOf('_linked_product_id') === 0) return;
        if ((elx.type === 'checkbox' || elx.type === 'radio') && !elx.checked) return;
        if (elx.value === '') return;
        props[key] = elx.value;
      });
      return props;
    }

    function atomicAdd(button) {
      var form = document.getElementById(formId);
      if (!form) return;
      var variantEl = form.querySelector('[name="id"]');
      var variantId = variantEl ? variantEl.value : null;
      if (!variantId) { console.warn('[wf-addons] no parent variant id'); return; }
      var qtyEl = form.querySelector('[name="quantity"]');
      var parentQty = qtyEl ? (parseInt(qtyEl.value, 10) || 1) : 1;
      var childrenOnly = set.checkoutMode === 'children_only';
      var group = uuid();
      var sel = selectedList();

      // Field values to carry as configurator props on child lines (children_only).
      var configProps = {};
      if (childrenOnly && set.fields) {
        set.fields.forEach(function (f) {
          var v = fieldValues[f.id];
          if (v != null && String(v).trim() !== '') configProps[f.propertyName] = v;
        });
      }

      var items = [];
      if (!childrenOnly) {
        var parentProps = gatherParentProps(form); // includes Add-Ons summary + field values
        parentProps._wf_addon_group = group;
        items.push({ id: variantId, quantity: parentQty, properties: parentProps });
      }
      sel.forEach(function (it) {
        var props = { _wf_addon_group: group, _wf_addon_child: 'true', 'For': productTitle };
        if (childrenOnly && /boutonni[eè]re|corsage/i.test(productTitle)) {
          var selectedTitle = variantTitles[variantId];
          if (typeof selectedTitle === 'string' && selectedTitle !== 'Default Title') {
            props['Colour Theme'] = selectedTitle;
          }
        }
        if (it.imageUrl) props._wf_addon_image = it.imageUrl;
        if (childrenOnly) { for (var k in configProps) props[k] = configProps[k]; }
        var item = { id: it.variantId, quantity: it.qty, properties: props };
        if (!childrenOnly) item.parent_id = variantId;
        items.push(item);
      });

      if (items.length === 0) { if (button) button.removeAttribute('aria-busy'); return; }

      var fd = new FormData();
      items.forEach(function (item, idx) {
        var base = 'items[' + idx + ']';
        fd.append(base + '[id]', item.id);
        fd.append(base + '[quantity]', String(item.quantity));
        if (item.parent_id) fd.append(base + '[parent_id]', item.parent_id);
        if (item.properties) {
          Object.keys(item.properties).forEach(function (k) {
            if (item.properties[k] != null) fd.append(base + '[properties][' + k + ']', item.properties[k]);
          });
        }
      });

      if (button) button.setAttribute('aria-busy', 'true');
      var addUrl = (window.Eurus && window.Eurus.cart_add_url) || '/cart/add.js';
      fetch(addUrl, {
        method: 'POST',
        headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body: fd
      })
        .then(function (r) {
          if (!r.ok) return r.json().then(function (j) { throw new Error(j && j.description || ('add failed ' + r.status)); });
          return r.json();
        })
        .then(function () {
          var cartUrl = (window.Eurus && window.Eurus.routes && window.Eurus.routes.cart_url) || '/cart';
          // Button stays aria-busy through the delay, so re-clicks remain blocked.
          setTimeout(function () { window.location.href = cartUrl; }, ADD_TO_CART_BEACON_MS);
        })
        .catch(function (err) {
          console.error('[wf-addons] atomic add failed:', err);
          if (button) button.removeAttribute('aria-busy');
          var hint = root.querySelector('.wf-product-addons__hint');
          if (hint) { hint.textContent = 'Could not add to cart, please try again.'; hint.classList.add('wf-product-addons__hint--flash'); }
        });
    }

    // ---- Live price preview (display-only) ---------------------------------

    // Read the live base price (cents) from the theme's own price node so we stay
    // in sync with variant changes; fall back to the embedded variant map.
    function currentBaseCents() {
      var tp = document.querySelector('#price-' + sectionId + ' .target-price');
      if (tp) { var c = parseInt((tp.textContent || '').replace(/[^\d]/g, ''), 10); if (!isNaN(c)) return c; }
      var form = document.getElementById(formId);
      var idInput = form && form.querySelector('input[name="id"]');
      var vid = idInput ? String(idInput.value) : null;
      if (vid && variantPrices[vid] != null) return Number(variantPrices[vid]);
      return basePrice;
    }

    function addonCents() {
      if (optedOut) return 0;
      var c = 0;
      for (var id in state) {
        var it = state[id];
        if (it.qty > 0 && it.price != null) c += Math.round(it.price * 100) * it.qty;
      }
      return c;
    }

    // Reflect base + selected add-ons in the main PDP price below the title
    // (display preview; Shopify charges per cart line).
    function updateMainPrice() {
      var block = document.getElementById('price-' + sectionId);
      if (!block) return;
      var p = block.querySelector('.main-product-price p.price') || block.querySelector('p.price') || block.querySelector('.main-product-price .price');
      if (!p) return;
      var gst = p.querySelector('.wf-product-price-gst-label');
      p.textContent = '';
      p.appendChild(document.createTextNode(' ' + money(currentBaseCents() + addonCents())));
      if (gst) p.appendChild(gst);
    }

    var lastVid = null;
    function watchVariant() {
      var eventName = 'eurus:product-page-variant-select:updated:' + sectionId;
      function syncVariantPrice() {
        var form = document.getElementById(formId);
        var idInput = form && form.querySelector('input[name="id"]');
        var vid = idInput ? String(idInput.value) : null;
        if (vid !== lastVid) { lastVid = vid; updateMainPrice(); }
      }
      function onVariantUpdated() {
        if (!root.isConnected) {
          document.removeEventListener(eventName, onVariantUpdated);
          return;
        }
        syncVariantPrice();
      }
      syncVariantPrice();
      document.addEventListener(eventName, onVariantUpdated);
    }

    // ---- Rendering --------------------------------------------------------

    function onItemChange(item, qty) {
      var prev = state[item.id] || { qty: 0 };
      var next = Math.max(0, Math.min(qty, item.maxQty));
      state[item.id] = { qty: next, variantId: item.variantId, title: item.title, price: item.price, imageUrl: item.imageUrl };
      if (next > 0) optedOut = false;
      // reflect opt-out state in UI when a real selection clears it
      if (next > 0) {
        var optEl = root.querySelector('.wf-addons-optout');
        if (optEl) optEl.classList.remove('wf-addons-optout--on');
      }
      syncForm();
      updateMainPrice();
      applyGate();
      return next !== prev.qty;
    }

    function renderStepper(item, descEl) {
      var wrap = el('div', 'wf-addon-stepper');
      var minus = el('button', 'wf-addon-stepper__btn', '−');
      minus.type = 'button';
      minus.setAttribute('aria-label', 'Decrease ' + item.title);
      var count = el('span', 'wf-addon-stepper__count', '0');
      var plus = el('button', 'wf-addon-stepper__btn', '+');
      plus.type = 'button';
      plus.setAttribute('aria-label', 'Increase ' + item.title);

      function refresh() {
        var q = (state[item.id] && state[item.id].qty) || 0;
        count.textContent = String(q);
        wrap.classList.toggle('wf-addon-stepper--active', q > 0);
        minus.disabled = q <= 0;
        plus.disabled = q >= item.maxQty;
        if (descEl) descEl.hidden = q <= 0; // reveal the product description once selected
      }
      minus.addEventListener('click', function () {
        var q = (state[item.id] && state[item.id].qty) || 0;
        onItemChange(item, q - 1); refresh();
      });
      plus.addEventListener('click', function () {
        var q = (state[item.id] && state[item.id].qty) || 0;
        onItemChange(item, q + 1); refresh();
      });
      refresh();
      wrap.appendChild(minus);
      wrap.appendChild(count);
      wrap.appendChild(plus);
      return wrap;
    }

    // Shopify CDN sizing param for sharper thumbs / larger modal image.
    function sized(url, w) {
      if (!url) return url;
      return url + (url.indexOf('?') !== -1 ? '&' : '?') + 'width=' + w;
    }

    // Lazy, shared image lightbox (Eurus-style: white card, dim backdrop, close).
    var modal = null;
    function ensureModal() {
      if (modal) return modal;
      modal = el('div', 'wf-addons-modal');
      modal.setAttribute('hidden', '');
      var inner = el('div', 'wf-addons-modal__inner');
      var closeBtn = el('button', 'wf-addons-modal__close', '×');
      closeBtn.type = 'button';
      closeBtn.setAttribute('aria-label', 'Close');
      var img = el('img', 'wf-addons-modal__img');
      var cap = el('div', 'wf-addons-modal__cap');
      inner.appendChild(closeBtn);
      inner.appendChild(img);
      inner.appendChild(cap);
      modal.appendChild(inner);
      function close() { modal.setAttribute('hidden', ''); }
      modal.addEventListener('click', function (e) { if (e.target === modal) close(); });
      closeBtn.addEventListener('click', close);
      document.addEventListener('keydown', function (e) { if (e.key === 'Escape') close(); });
      document.body.appendChild(modal);
      modal.__img = img;
      modal.__cap = cap;
      return modal;
    }
    function openModal(item) {
      var m = ensureModal();
      m.__img.src = sized(item.imageUrl, 1200);
      m.__img.alt = item.title;
      m.__cap.textContent = item.price != null ? item.title + '  +' + money(Math.round(item.price * 100)) : item.title;
      m.removeAttribute('hidden');
    }

    function renderCard(item) {
      var card = el('div', 'wf-addon-card');
      if (!item.variantId) card.classList.add('wf-addon-card--unavailable');

      var media;
      if (item.imageUrl) {
        media = el('button', 'wf-addon-card__media');
        media.type = 'button';
        var img = el('img', 'wf-addon-card__img');
        img.src = sized(item.imageUrl, 320);
        img.alt = item.title;
        img.loading = 'lazy';
        media.appendChild(img);
        media.appendChild(el('span', 'wf-addon-card__zoom'));
        media.setAttribute('aria-label', 'View larger image of ' + item.title);
        media.addEventListener('click', function () { openModal(item); });
      } else {
        // No catalog image — show a tasteful florist placeholder, not an empty box.
        media = el('div', 'wf-addon-card__media wf-addon-card__media--empty');
        media.setAttribute('role', 'img');
        media.setAttribute('aria-label', item.title);
        media.innerHTML = '<svg viewBox="0 0 24 24" width="30" height="30" fill="currentColor" aria-hidden="true">' +
          '<circle cx="12" cy="6" r="3"/><circle cx="12" cy="18" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="12" r="3"/><circle cx="12" cy="12" r="3.4"/></svg>';
      }
      card.appendChild(media);

      card.appendChild(el('div', 'wf-addon-card__title', item.title));

      // Product description, revealed below the title once the add-on is selected
      // (qty > 0). Plain text from the worker (HTML already stripped).
      var descEl = null;
      if (item.description) {
        descEl = el('div', 'wf-addon-card__desc', item.description);
        descEl.hidden = true;
        card.appendChild(descEl);
      }

      if (item.price != null) {
        card.appendChild(el('div', 'wf-addon-card__price', '+' + money(Math.round(item.price * 100))));
      }
      if (item.variantId) {
        card.appendChild(renderStepper(item, descEl));
      } else {
        card.appendChild(el('span', 'wf-addon-card__na', 'Unavailable'));
      }
      return card;
    }

    function renderCategory(cat) {
      // Display-only text/help block (not an accordion of products).
      if (cat.kind === 'text') {
        var info = el('div', 'wf-addon-info');
        if (cat.name) info.appendChild(el('div', 'wf-addon-info__title', cat.name));
        var infoBody = el('div', 'wf-addon-info__body');
        infoBody.textContent = cat.bodyText || ''; // textContent + CSS white-space keeps line breaks, no HTML injection
        info.appendChild(infoBody);
        return info;
      }

      var section = el('div', 'wf-addon-cat');
      var head = el('button', 'wf-addon-cat__head');
      head.type = 'button';
      head.appendChild(el('span', 'wf-addon-cat__name', cat.name));
      head.appendChild(el('span', 'wf-addon-cat__chev'));

      var panel = el('div', 'wf-addon-cat__panel');
      var grid = el('div', 'wf-addon-grid');
      cat.items.forEach(function (item) { grid.appendChild(renderCard(item)); });
      panel.appendChild(grid);

      var open = !cat.collapsedByDefault;
      function applyOpen() {
        section.classList.toggle('wf-addon-cat--open', open);
        head.setAttribute('aria-expanded', String(open));
      }
      head.addEventListener('click', function () { open = !open; applyOpen(); });
      applyOpen();

      section.appendChild(head);
      section.appendChild(panel);
      return section;
    }

    function renderOptOut() {
      var row = el('button', 'wf-addons-optout');
      row.type = 'button';
      row.appendChild(el('span', 'wf-addons-optout__mark'));
      row.appendChild(el('span', 'wf-addons-optout__label', 'No thanks'));
      row.addEventListener('click', function () {
        optedOut = !optedOut;
        row.classList.toggle('wf-addons-optout--on', optedOut);
        if (optedOut) {
          // clear all selections
          for (var id in state) state[id].qty = 0;
          root.querySelectorAll('.wf-addon-stepper').forEach(function (s) {
            var c = s.querySelector('.wf-addon-stepper__count');
            if (c) c.textContent = '0';
            s.classList.remove('wf-addon-stepper--active');
            var btns = s.querySelectorAll('.wf-addon-stepper__btn');
            if (btns[0]) btns[0].disabled = true;
          });
        }
        syncForm();
        updateMainPrice();
        applyGate();
      });
      return row;
    }

    function renderField(f) {
      var wrap = el('div', 'wf-addon-field');
      var inputId = 'wf-field-' + f.id;
      var lbl = el('label', 'wf-addon-field__label', f.label + (f.required ? ' *' : ''));
      lbl.setAttribute('for', inputId);
      wrap.appendChild(lbl);

      var input;
      if (f.type === 'select') {
        input = el('select', 'wf-addon-field__input');
        var ph = el('option', null, f.placeholder || 'Select…');
        ph.value = '';
        input.appendChild(ph);
        (f.options || []).forEach(function (o) {
          var opt = el('option', null, o);
          opt.value = o;
          input.appendChild(opt);
        });
      } else if (f.type === 'text') {
        input = el('input', 'wf-addon-field__input');
        input.type = 'text';
        if (f.placeholder) input.placeholder = f.placeholder;
        if (f.maxLength) input.maxLength = f.maxLength;
      } else {
        input = el('textarea', 'wf-addon-field__input');
        input.rows = 3;
        if (f.placeholder) input.placeholder = f.placeholder;
        if (f.maxLength) input.maxLength = f.maxLength;
      }
      input.id = inputId;
      if (f.required) input.setAttribute('aria-required', 'true');
      var onInput = function () { fieldValues[f.id] = input.value; syncForm(); applyGate(); };
      input.addEventListener('input', onInput);
      input.addEventListener('change', onInput);

      wrap.appendChild(input);
      return wrap;
    }

    function render(resolved) {
      set = resolved;
      var hasCats = set.categories && set.categories.length > 0;
      var hasFields = set.fields && set.fields.length > 0;

      var defaultHint = hasCats ? 'Pick an add-on or choose "No thanks" to continue.' : 'Please complete the details below.';
      var showHeader = set.showHeader !== false;
      var showSubtitle = set.showSubtitle !== false;
      if (showHeader || showSubtitle) {
        var header = el('div', 'wf-product-addons__header');
        if (showHeader) header.appendChild(el('h3', 'wf-product-addons__title', set.headerText || 'Make it extra special'));
        if (showSubtitle) header.appendChild(el('p', 'wf-product-addons__hint', set.subtitleText || defaultHint));
        body.appendChild(header);
      }

      if (hasCats) {
        set.categories.forEach(function (cat) { body.appendChild(renderCategory(cat)); });
        // "No thanks" goes after the categories, just above Add to Cart (operator call
        // 2026-09-23, reversing the 09-19 top placement — docs/addons.md).
        // Sets flagged selection_required force a choice — no opt-out offered.
        if (!set.selectionRequired) body.appendChild(renderOptOut());
      }

      if (hasFields) {
        var fieldsWrap = el('div', 'wf-addon-fields');
        set.fields.forEach(function (f) { fieldsWrap.appendChild(renderField(f)); });
        body.appendChild(fieldsWrap);
      }

      root.hidden = false;
      // Re-measure every render so the memo self-corrects when a set changes size.
      requestAnimationFrame(function () { rememberHeight(root.getBoundingClientRect().height); });
      syncForm();        // write default Add-Ons property + any field values
      updateMainPrice();
      attachGuard();
      applyGate();
      watchVariant();
    }
  }

  function boot() {
    var roots = document.querySelectorAll('[data-wf-addons]');
    for (var i = 0; i < roots.length; i++) init(roots[i]);
  }

  // Re-hydrate when the quick-view popup injects its product HTML (via innerHTML,
  // so the mount appears after our initial boot and any inline <script> never runs).
  // init() is idempotent (root.__wfAddonsInit), so re-running boot() is safe.
  function watchQuickView() {
    if (typeof MutationObserver === 'undefined') return;
    function attach(el) { new MutationObserver(boot).observe(el, { childList: true }); }
    var qv = document.getElementById('quickview-product-content');
    if (qv) { attach(qv); return; }
    var bodyObs = new MutationObserver(function () {
      var el = document.getElementById('quickview-product-content');
      if (el) { bodyObs.disconnect(); attach(el); }
    });
    bodyObs.observe(document.documentElement, { childList: true, subtree: true });
  }

  function start() { boot(); watchQuickView(); }
  if (document.readyState !== 'loading') start();
  else document.addEventListener('DOMContentLoaded', start);
})();
